import type { JSX } from 'react';
import { Box, Stack } from '@mui/material';
import { usePeripheryDetailsOutlet } from './CloudConnectorPeripheriesDetail.tsx';
import {
    PeripheryDetailSeparator,
    PeripheryDetailTitle,
    PeripheryGrid,
} from './CloudConnectorPeripheryDetailTabPresence.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Navigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { EmDash, Zwsp } from 'dg-web-shared/lib/Punctuation.ts';
import {
    PayStationPaymentMethod,
    PayStationPaymentState,
} from 'dg-web-shared/model/PayStationPayment.ts';

export function PeripheryPaymentsTab({ primary }: { primary?: boolean }) {
    const {
        peripheryConfig: { payments },
    } = usePeripheryDetailsOutlet();

    if (!payments) {
        return <Navigate to={'../detail'} />;
    }

    const runningTransaction = payments.runningTransaction;
    const lastCompletedTransactions = payments.lastCompletedTransactions;
    const runningTransactionIdentification = runningTransaction
        ? runningTransaction.entryNameIfZoneHasMultiple
            ? `${runningTransaction.identificationString} — ${runningTransaction.entryNameIfZoneHasMultiple}`
            : runningTransaction.identificationString
        : Zwsp;

    return (
        <Stack
            direction={'column'}
            sx={{
                display: 'flex',
                flexGrow: 1,
                padding: theme => theme.spacing(3),
                color: theme =>
                    primary
                        ? theme.palette.primary.main
                        : theme.palette.grey.A700,
            }}
        >
            <PeripheryDetailTitle primary>
                <Localized
                    de="Laufend"
                    fr="En cours"
                    it="In corso"
                    en="On going"
                />
            </PeripheryDetailTitle>
            <PeripheryGrid
                primary
                topLeft={
                    runningTransaction
                        ? Formatter.dayMonthYearHourMinute(
                              DateTime.fromISO(runningTransaction.quotedAt),
                          )
                        : EmDash
                }
                bottomLeft={runningTransactionIdentification}
                topRight={
                    runningTransaction
                        ? runningTransaction.amount.toLocaleString('de-CH', {
                              style: 'currency',
                              currency: 'CHF',
                          })
                        : EmDash
                }
                bottomRight={undefined}
                noPadding
            />
            <PeripheryDetailSeparator />
            <PeripheryDetailTitle>
                <Localized
                    de="Abgeschlossene"
                    fr="Terminés"
                    it="Conclusi"
                    en="Closed"
                />
            </PeripheryDetailTitle>
            {lastCompletedTransactions.map(transaction => (
                <PeripheryGrid
                    key={transaction.payStationPaymentId}
                    topLeft={Formatter.dayMonthYearHourMinute(
                        DateTime.fromISO(transaction.quotedAt),
                    )}
                    bottomLeft={
                        transaction.entryNameIfZoneHasMultiple
                            ? `${transaction.identificationString} — ${transaction.entryNameIfZoneHasMultiple}`
                            : transaction.identificationString
                    }
                    topRight={transaction.amount.toLocaleString('de-CH', {
                        style: 'currency',
                        currency: 'CHF',
                    })}
                    bottomRight={
                        <PaymentStateText
                            state={transaction.state}
                            amount={transaction.amount}
                            paymentMethod={transaction.paymentMethod}
                        />
                    }
                />
            ))}
            {lastCompletedTransactions.length < 1 && (
                <Box sx={{ textAlign: 'center', paddingTop: '4px' }}>
                    <Localized
                        de="Es gibt noch keine abgeschlossenen Transaktionen."
                        fr="Il n’y a pas encore de transactions terminées."
                        it="Non ci sono ancora transazioni completate."
                        en="There are no completed transactions yet."
                    />
                </Box>
            )}
        </Stack>
    );
}

function PaymentStateText({
    state,
    amount,
    paymentMethod,
}: {
    state: PayStationPaymentState;
    amount: number;
    paymentMethod: PayStationPaymentMethod | null;
}): JSX.Element {
    if (!amount) {
        return (
            <Localized
                de="keine Zahlung"
                fr="aucun paiement"
                it="nessun pagamento"
                en="no payment"
            />
        );
    }
    switch (state) {
        case PayStationPaymentState.QUOTED:
            return (
                <Localized
                    de="laufend"
                    fr="en cours"
                    it="in corso"
                    en="ongoing"
                />
            );
        case PayStationPaymentState.ABORTED:
            return (
                <Localized
                    de="abgebrochen"
                    fr="annulé"
                    it="annullato"
                    en="aborted"
                />
            );
        case PayStationPaymentState.PAID:
            return (
                <>
                    <Localized de="bezahlt" fr="payé" it="pagato" en="paid" />
                    {paymentMethod && (
                        <>
                            {' ('}
                            <PaymentMethodText paymentMethod={paymentMethod} />
                            {')'}
                        </>
                    )}
                </>
            );
    }
}

function PaymentMethodText({
    paymentMethod,
}: {
    paymentMethod: PayStationPaymentMethod;
}) {
    switch (paymentMethod) {
        case PayStationPaymentMethod.CASH:
            return (
                <Localized de="bargeld" fr="espèces" it="contanti" en="cash" />
            );
        case PayStationPaymentMethod.CASHLESS:
            return (
                <Localized
                    de="bargeldlos"
                    fr="sans espèces"
                    it="senza contanti"
                    en="cashless"
                />
            );
    }
}
